import React from "react";
import { Phone, MapPin, Clock, ExternalLink } from "lucide-react";

const MAP_QUERY = "Telleixako+Benta+Bergara+Gipuzkoa";
// Use business name query — Google auto-places the pin at the exact restaurant.
// Wider zoom (z=15) so the red pin is visible below the info card.
const MAP_EMBED = `https://maps.google.com/maps?q=${MAP_QUERY}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
const MAP_LINK = `https://www.google.com/maps?cid=17847853253191658888`;

export default function Contact({ t }) {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#F5F2EB] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <div className="mb-14">
          <p
            data-testid="contact-eyebrow"
            className="mb-6 font-body text-xs uppercase tracking-[0.4em] text-[#5C3A21]"
          >
            — {t.contact.eyebrow}
          </p>
          <h2
            data-testid="contact-title"
            className="font-serif-display text-5xl leading-[1.05] text-[#1A1A1A] sm:text-6xl"
          >
            {t.contact.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Map */}
          <div className="lg:col-span-7">
            <div
              data-testid="contact-map"
              className="relative h-[420px] w-full overflow-hidden border border-[#1A1A1A]/15 sm:h-[520px]"
            >
              <iframe
                title="Telleixako Benta map"
                src={MAP_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <a
              href={MAP_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-directions-button"
              className="mt-4 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.3em] text-[#5C3A21] hover:text-[#1A1A1A]"
            >
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t.contact.directionsBtn}
            </a>
          </div>

          {/* Details */}
          <div className="lg:col-span-5">
            <div className="space-y-10">
              <div data-testid="contact-address">
                <div className="mb-3 flex items-center gap-2 text-[#5C3A21]">
                  <MapPin className="h-4 w-4" strokeWidth={1.5} />
                  <span className="font-body text-xs uppercase tracking-[0.3em]">
                    Bergara · Gipuzkoa
                  </span>
                </div>
                <p className="font-serif-display text-2xl leading-snug text-[#1A1A1A] sm:text-3xl">
                  {t.contact.address}
                </p>
              </div>

              <div data-testid="contact-phone">
                <div className="mb-3 flex items-center gap-2 text-[#5C3A21]">
                  <Phone className="h-4 w-4" strokeWidth={1.5} />
                  <span className="font-body text-xs uppercase tracking-[0.3em]">
                    {t.contact.phoneLabel}
                  </span>
                </div>
                <a
                  href="tel:+34943291730"
                  data-testid="contact-phone-link"
                  className="font-serif-display text-3xl italic text-[#1A1A1A] hover:text-[#5C3A21] sm:text-4xl"
                >
                  {t.contact.phone}
                </a>
              </div>

              <div data-testid="contact-schedule">
                <div className="mb-3 flex items-center gap-2 text-[#5C3A21]">
                  <Clock className="h-4 w-4" strokeWidth={1.5} />
                  <span className="font-body text-xs uppercase tracking-[0.3em]">
                    {t.contact.scheduleTitle}
                  </span>
                </div>
                <div className="divide-y divide-[#1A1A1A]/15 border-y border-[#1A1A1A]/15">
                  <div className="flex items-baseline justify-between py-4">
                    <span className="font-serif-display text-xl text-[#1A1A1A]">
                      {t.contact.scheduleWeekdays}
                    </span>
                    <span className="font-body text-sm tracking-wider text-[#4A4A4A]">
                      {t.contact.scheduleWeekdaysHours}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between py-4">
                    <span className="font-serif-display text-xl text-[#1A1A1A]">
                      {t.contact.scheduleWeekend}
                    </span>
                    <span className="font-body text-sm uppercase tracking-[0.2em] text-[#A63D2F]">
                      {t.contact.scheduleClosed}
                    </span>
                  </div>
                </div>
              </div>

              <a
                href="tel:+34943291730"
                data-testid="contact-call-cta"
                className="inline-flex w-full items-center justify-center gap-3 bg-[#1A1A1A] px-7 py-5 text-[#F5F2EB] transition-all hover:bg-[#5C3A21]"
              >
                <Phone className="h-4 w-4" strokeWidth={1.5} />
                <span className="font-body text-sm uppercase tracking-[0.3em]">
                  {t.hero.callBtn}
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
